import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getAdvisors } from '../../../api/enrollments/get-advisors'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getOptions } from '../../../api/options/get-options'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTripPlaces } from '../../../api/trip-places/get-trip-places'
import {
  PaymentStatusEnum,
  PaymentStatusEnumMapping
} from '../../../lib/types/enums/enrollment/payment-status.enum'
import {
  OrigineEnum,
  origineEnumMapping
} from '../../../lib/types/enums/information-requests/origin.enum'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../elements/country-flag'

interface Props {
  version: 'quote' | 'enrollment'
  filter?: EnrollmentFilter
  onFilterChange: (filter: EnrollmentFilter) => void
}

export function EnrollmentsFilter({ version, filter, onFilterChange }: Props) {
  function setFilter<K extends keyof EnrollmentFilter>(key: K, value: EnrollmentFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: options } = useQuery({
    queryKey: ['options', { version: 'LIGHT' }],
    queryFn: () => getOptions({ version: 'LIGHT' })
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: advisers } = useQuery({
    queryKey: ['advisors'],
    queryFn: () => getAdvisors()
  })

  const { data: tripPlaces } = useQuery({
    queryKey: ['trip-place'],
    queryFn: () => getTripPlaces()
  })

  if (!filter) return null

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <MultiSelectInput
        searchable
        label={'Période(s)'}
        variant={'warning'}
        options={periods?.data.map((period) => ({
          label: period.name,
          value: period.name
        }))}
        selectedOptions={filter.periods?.map((period) => ({
          label: period,
          value: period
        }))}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <DateStringInput
        label="Date de départ"
        value={filter.departureDate}
        onChange={(date) => onFilterChange({ ...filter, departureDate: date })}
      />

      <SelectInput
        label={'Pays'}
        searchable
        options={countries?.data
          .map((country) => ({
            label: country.name,
            value: country.code,
            custom: <CountryFlag code={country.code} label={country.name} />
          }))
          .sort((a, b) => a.label.localeCompare(b.label))}
        selectedOption={conditionalRender(!!filter.country, {
          value: filter.country,
          label: countries?.data.find((country) => country.code === filter.country)?.name ?? ''
        })}
        onChange={(value) => setFilter('country', value)}
      />

      <MultiSelectInput
        searchable
        label={'Formule'}
        variant={'primary'}
        options={formulas?.data.map((formula) => ({
          label: getTranslatedName(formula),
          value: getTranslatedName(formula)
        }))}
        selectedOptions={filter.formulas?.map((formula) => ({
          label: formula,
          value: formula
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
      />

      <MultiSelectInput
        searchable
        label={'Thème(s) / Option(s)'}
        variant={'primary'}
        options={
          options?.data.map((option) => ({
            label: getTranslatedName(option),
            value: option.id
          })) ?? []
        }
        selectedOptions={filter?.options?.map((item) => ({
          label: getTranslatedName(options?.data.find((option) => option.id === item)),
          value: item
        }))}
        onChange={(options) => setFilter('options', options)}
      />

      {version === 'enrollment' && (
        <>
          <MultiSelectInput
            searchable
            label={'Villes de départ'}
            variant={'primary'}
            onChange={(tripPlaces) => setFilter('tripPlaces', tripPlaces)}
            options={tripPlaces?.data.map((tripPlace) => ({
              label: getTranslatedName(tripPlace),
              value: getTranslatedName(tripPlace)
            }))}
            selectedOptions={filter.tripPlaces?.map((tripPlace) => ({
              label: tripPlace,
              value: tripPlace
            }))}
          />

          <SelectInput
            label={'Moyen de transport'}
            searchable
            options={transportMeans?.data?.map((transportMean) => ({
              label: getTranslatedName(transportMean),
              value: transportMean.id
            }))}
            onChange={(transportMean) => setFilter('transportMean', transportMean)}
            selectedOption={conditionalRender(!!filter.transportMean, {
              label: getTranslatedName(
                transportMeans?.data?.find((tm) => tm.id === filter.transportMean)
              ),
              value: filter.transportMean
            })}
          />

          <SelectInput
            label={'Statut du paiement'}
            searchable
            options={Object.values(PaymentStatusEnum).map((status) => ({
              label: PaymentStatusEnumMapping[status].label,
              value: status
            }))}
            onChange={(paymentStatus) => setFilter('paymentStatus', paymentStatus)}
            selectedOption={conditionalRender(
              !!filter.paymentStatus,
              filter.paymentStatus
                ? {
                    label: PaymentStatusEnumMapping[filter.paymentStatus].label,
                    value: filter.paymentStatus
                  }
                : undefined
            )}
          />

          <DateStringInput
            label="Date d'inscription"
            value={filter.createdAt}
            onChange={(date) => onFilterChange({ ...filter, createdAt: date })}
          />

          <Input
            type="text"
            label={'Source'}
            value={filter.source}
            onChange={(e) => setFilter('source', e.target.value)}
          />

          <SelectInput
            label={'Origine'}
            searchable
            options={Object.values(OrigineEnum).map((origin) => ({
              label: origineEnumMapping[origin].label,
              value: origin
            }))}
            onChange={(origin) => setFilter('origin', origin)}
            selectedOption={conditionalRender(
              !!filter.origin,
              filter.origin
                ? {
                    label: origineEnumMapping[filter.origin].label,
                    value: filter.origin
                  }
                : undefined
            )}
          />

          <SelectInput
            searchable
            label="Suivi par"
            onChange={(adviserId) => setFilter('attributedUserId', adviserId)}
            options={advisers?.map((adviser) => ({
              label: toFullName(adviser.firstName, adviser.lastName),
              value: adviser.id
            }))}
            selectedOption={conditionalRender(!!filter.attributedUserId, {
              value: filter.attributedUserId,
              label: toFullName(
                advisers?.find((adviser) => adviser.id === filter.attributedUserId)?.firstName,
                advisers?.find((adviser) => adviser.id === filter.attributedUserId)?.lastName
              )
            })}
          />
        </>
      )}
    </div>
  )
}
